import { Link } from "react-router-dom";
import { Field, inputProps, Honeypot, Consent, isEmail, isPhone } from "./Fields";
import { useLeadForm, SubmitButton, FormError, Confirmation } from "./useLeadForm";

export const SECTORS = ["Restaurant / café", "Beauté / bien-être", "Coiffure / barbier", "Commerce / boutique", "Artisan / indépendant", "Autre"];
export const GOALS = ["Visibilité Google", "Création de site", "Refonte de site", "Réservations / prises de rendez-vous", "Autre"];

const initial = { name: "", company: "", sector: "", city: "", website: "", google_profile: "", goals: [], email: "", phone: "", message: "" };

function validate(v) {
  const e = {};
  if (v.name.trim().length < 2) e.name = "Indiquez votre nom et prénom.";
  if (!v.company.trim()) e.company = "Indiquez le nom de votre entreprise.";
  if (!v.sector) e.sector = "Choisissez votre secteur d'activité.";
  if (v.city.trim().length < 2) e.city = "Indiquez votre ville.";
  if (!v.goals.length) e.goals = "Sélectionnez au moins un objectif.";
  if (!isEmail(v.email)) e.email = "Saisissez une adresse e-mail valide, par exemple nom@entreprise.fr.";
  if (!isPhone(v.phone)) e.phone = "Ce numéro ne semble pas valide.";
  return e;
}

export default function DiagnosticForm() {
  const { values: v, errors: er, status, serverError, set, submit } = useLeadForm("/diagnostic", initial, validate);

  if (status === "sent") {
    return (
      <Confirmation title="Merci, votre demande est bien transmise." testid="diagnostic-confirmation">
        <p>Elle m'a bien été transmise. Prochaine étape : j'étudie votre présence en ligne à partir de ces éléments, puis je reviens vers vous par e-mail pour convenir d'un échange.</p>
        <p>Je réalise moi-même chaque diagnostic, sans outil automatique.</p>
        <p><Link to="/referencement-local-toulouse" className="underline">En attendant, découvrez comment fonctionne la recherche locale.</Link></p>
      </Confirmation>
    );
  }

  const toggleGoal = (g) => set("goals", v.goals.includes(g) ? v.goals.filter((x) => x !== g) : [...v.goals, g]);

  return (
    <form onSubmit={submit} noValidate className="relative space-y-9" data-testid="diagnostic-form" aria-label="Formulaire de diagnostic digital">
      <Honeypot value={v.hp} onChange={set} />
      <fieldset className="space-y-7">
        <legend className="label mb-2">01 — Vous</legend>
        <div className="grid gap-7 md:grid-cols-2">
          <Field id="name" label="Nom et prénom" error={er.name}><input {...inputProps("name", v.name, set, er.name, { autoComplete: "name" })} /></Field>
          <Field id="company" label="Nom de l'entreprise" error={er.company}><input {...inputProps("company", v.company, set, er.company, { autoComplete: "organization" })} /></Field>
          <Field id="email" label="Adresse e-mail professionnelle" error={er.email}><input type="email" {...inputProps("email", v.email, set, er.email, { autoComplete: "email", inputMode: "email" })} /></Field>
          <Field id="phone" label="Téléphone" optional error={er.phone}><input type="tel" {...inputProps("phone", v.phone, set, er.phone, { autoComplete: "tel", inputMode: "tel" })} /></Field>
        </div>
      </fieldset>

      <fieldset className="space-y-7">
        <legend className="label mb-2">02 — Votre activité</legend>
        <div className="grid gap-7 md:grid-cols-2">
          <Field id="sector" label="Secteur d'activité" error={er.sector}>
            <select {...inputProps("sector", v.sector, set, er.sector)} className="field appearance-none">
              <option value="">Sélectionner…</option>
              {SECTORS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field id="city" label="Ville" error={er.city}><input {...inputProps("city", v.city, set, er.city, { autoComplete: "address-level2" })} /></Field>
          <Field id="website" label="Site internet actuel" optional hint="Laissez vide si vous n'avez pas encore de site."><input type="url" {...inputProps("website", v.website, set, null, { placeholder: "https://", inputMode: "url" })} /></Field>
          <Field id="google_profile" label="Fiche Google Business Profile" optional hint="Lien ou nom exact de l'établissement sur Google Maps."><input {...inputProps("google_profile", v.google_profile, set, null)} /></Field>
        </div>
      </fieldset>

      <fieldset aria-describedby={er.goals ? "goals-error" : undefined}>
        <legend className="label mb-4">03 — Vos objectifs principaux <span aria-hidden="true">*</span></legend>
        <div className="flex flex-wrap gap-2.5" data-testid="field-goals">
          {GOALS.map((g) => {
            const on = v.goals.includes(g);
            return (
              <button key={g} type="button" aria-pressed={on} onClick={() => toggleGoal(g)} data-testid={`goal-${GOALS.indexOf(g)}`}
                className={`min-h-[44px] border px-5 text-[14px] font-medium transition-colors duration-300 ${on ? "border-ink bg-ink text-ivory" : "border-ink/30 hover:border-ink"}`}>
                {g}
              </button>
            );
          })}
        </div>
        {er.goals && <p id="goals-error" role="alert" className="mt-2 text-[13px] font-medium text-red-800">{er.goals}</p>}
      </fieldset>

      <Field id="message" label="Message complémentaire" optional hint="Votre contexte, vos questions, vos échéances…">
        <textarea rows={4} {...inputProps("message", v.message, set, null)} className="field resize-y" />
      </Field>

      <Consent checked={v.consent} onChange={set} error={er.consent} />
      <FormError message={serverError} />
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <SubmitButton status={status} testid="diagnostic-submit">Demander mon diagnostic</SubmitButton>
        <p className="text-[13px] text-mute">Les champs marqués * sont obligatoires.</p>
      </div>
    </form>
  );
}
